import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { t } from '@lingui/core/macro';
import Stack from '@mui/material/Stack';
import { PageHeader } from '@/common/components/page-header/PageHeader.tsx';
import { Trans } from '@lingui/react/macro';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { dispenserQueries, dispenserQueryKeys } from '@/features/dispensers/queries.ts';
import { DispenserForm } from '@/features/dispensers/components/dispenser-form/DispenserForm.tsx';
import { DispenserFormOutput } from '@/features/dispensers/components/dispenser-form/config.ts';
import { DispenserService } from '@/features/dispensers/services/DispenserService.ts';
import { FormPageSkeleton } from '@/common/components/loading-skeleton/FormPageSkeleton.tsx';
import { collectorQueries } from '@/features/collectors/queries.ts';
import { posQueries } from '@/features/pos/queries.ts';
import { producerQueries } from '@/features/producers/queries.ts';

export const Route = createFileRoute('/dispensers/$dispenserId/edit')({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(dispenserQueries.detail(params.dispenserId)),
      context.queryClient.ensureQueryData(collectorQueries.list()),
      context.queryClient.ensureQueryData(posQueries.list()),
      context.queryClient.ensureQueryData(producerQueries.list()),
    ]);

    return {
      breadcrumb: t`Edit`,
    };
  },
  pendingComponent: FormPageSkeleton,
});

function RouteComponent() {
  const params = Route.useParams();
  const { data: dispenser } = useSuspenseQuery(dispenserQueries.detail(params.dispenserId));
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleSubmit = async (values: DispenserFormOutput) => {
    await DispenserService.update({
      ...values,
      id: dispenser.id,
    });
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: dispenserQueryKeys.list(),
      }),
      queryClient.invalidateQueries({
        queryKey: dispenserQueryKeys.detail(dispenser.id),
      }),
    ]);
    await navigate({
      to: '/dispensers/$dispenserId',
      params: { dispenserId: `${dispenser.id}` },
      ignoreBlocker: true,
    });
  };

  const handleCancel = () => {
    void navigate({ to: '/dispensers/$dispenserId', params: { dispenserId: `${dispenser.id}` } });
  };

  return (
    <Stack gap={4}>
      <PageHeader
        title={<Trans>Edit Dispenser</Trans>}
        subtitle={<Trans>Update dispenser information and configuration</Trans>}
      />
      <DispenserForm
        edit
        initialValues={{
          sn: dispenser.sn,
          typeId: dispenser.type?.id ?? null,
          pos: dispenser.pos?.id ? `${dispenser.pos.id}` : null,
          canisters: dispenser.canisters,
          note: dispenser.note,
          producerId: dispenser.type.producer?.id ?? null,
          collectorId: dispenser.collector?.id ?? null,
        }}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </Stack>
  );
}
